import React, { useEffect, useState } from "react";
import { Footer, Navbar } from "../components";
import { useParams, Link } from "react-router-dom";
import api from "../utils/base_url";
import "../styles/OrderConfirmation.css";

const OrderConfirmation = () => {
  const { orderId } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchOrder = async () => {
      try {
        const res = await api.get(`orderdetails/${orderId}/`);
        setOrder(res.data.data);
      } catch (err) {
        console.error("Failed to fetch order", err);
      } finally {
        setLoading(false);
      }
    };
    fetchOrder();
  }, [orderId]);

  /* ── Helpers ── */
  const formatStatus = (value) => {
    if (!value) return "";
    return value.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
  };

  const statusKey = (status) => {
    const valid = ["pending", "order_confirmed", "shipped", "delivered", "cancelled", "returned"];
    return valid.includes(status) ? status : "pending";
  };

  /* ── States ── */
  if (loading) return (
    <><Navbar />
      <div className="oc-state-center">
        <p className="oc-state-title">Loading order details…</p>
      </div>
    <Footer /></>
  );

  if (!order) return (
    <><Navbar />
      <div className="oc-state-center">
        <p className="oc-state-title">Order not found</p>
        <Link to="/" className="oc-btn-primary">Go to Home</Link>
      </div>
    <Footer /></>
  );

  const productTotal = Number(order.subtotal || 0) + Number(order.tax || 0);

  return (
    <>
      <Navbar />
      <div className="oc-page">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-12 col-md-10 col-lg-8">
              <div className="oc-card">

                {/* ── Success Banner ── */}
                <div className="oc-banner">
                  <div className="oc-banner-icon">✓</div>
                  <p className="oc-banner-title">Order Confirmed!</p>
                  <p className="oc-banner-sub">
                    Thank you, <strong>{order.customer_name}</strong> — order{" "}
                    <strong>#{order.order_number}</strong> has been placed.
                  </p>
                </div>

                <div className="oc-body">

                  {/* ── Order Details ── */}
                  <div>
                    <p className="oc-section-label">Order details</p>
                    <div className="oc-meta-grid">

                      <div className="oc-meta-item">
                        <span className="oc-meta-key">Status :</span>
                        <span className={`oc-status-badge ${statusKey(order.status)}`}>
                          {formatStatus(order.status)}
                        </span>
                      </div>

                      <div className="oc-meta-item">
                        <span className="oc-meta-key">Ordered at :</span>
                        <span className="oc-meta-val">
                          {new Date(order.ordered_at).toLocaleString()}
                        </span>
                      </div>

                      <div className="oc-meta-item">
                        <span className="oc-meta-key">Payment method :</span>
                        <span className="oc-meta-val">
                          {order.payment_method === "cod" ? "Cash on Delivery" : "Online Payment"}
                        </span>
                      </div>

                      <div className="oc-meta-item">
                        <span className="oc-meta-key">Payment status :</span>
                        <span className={`oc-payment-badge ${order.payment_status}`}>
                          {formatStatus(order.payment_status)}
                        </span>
                      </div>

                      <div className="oc-meta-item-address">
                        <span className="oc-meta-key">Shipping address</span>
                        <span className="oc-meta-val">{order.shipping_address}</span>
                      </div>

                      <div className="oc-meta-item-address">
                        <span className="oc-meta-key">Billing address</span>
                        <span className="oc-meta-val">{order.billing_address}</span>
                      </div>

                    </div>
                  </div>

                  {/* ── Items ── */}
                  <div>
                    <p className="oc-section-label">Order items</p>
                    <div className="oc-table-wrap">
                      <table className="oc-table">
                        <thead>
                          <tr>
                            <th>Product</th>
                            <th>Variant</th>
                            <th>Qty</th>
                            <th>Price</th>
                            <th>Total</th>
                          </tr>
                        </thead>
                        <tbody>
                          {order.items.map((item, idx) => (
                            <tr key={idx}>
                              <td>{item.product_name}</td>
                              <td className="oc-td-muted">
                                {["size", "color"].includes(item.quantity_unit?.toLowerCase())
                                  ? item.quantity_value
                                  : `${Number(item.quantity_value)} ${item.quantity_unit}`}
                              </td>
                              <td>{item.quantity}</td>
                              <td className="oc-td-amount">₹{item.price.toLocaleString()}</td>
                              <td className="oc-td-amount">₹{item.total.toLocaleString()}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* ── Order Summary ── */}
                  <div>
                    <p className="oc-section-label">Order summary</p>
                    <div className="oc-summary">
                      <div className="oc-summary-row">
                        <span>Subtotal</span>
                        <span className="oc-summary-amount">₹{productTotal.toLocaleString()}</span>
                      </div>
                      <div className="oc-summary-row">
                        <span>Shipping</span>
                        <span className="oc-summary-amount">
                          ₹{order.shipping_cost.toLocaleString()}
                        </span>
                      </div>
                      <div className="oc-summary-row total">
                        <span>Total</span>
                        <span className="oc-summary-amount">
                          ₹{order.total_amount.toLocaleString()}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* ── CTA ── */}
                  <div className="oc-actions">
                    <Link to="/" className="oc-btn-primary">
                      Continue shopping
                    </Link>
                  </div>

                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default OrderConfirmation;